import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getCalendarDayByDate,
  getCalendarDays,
  getFirstAvailableSlot,
  type CalendarDay,
} from '../helpers/calendarHelper';
import {
  type AvailableService,
  type ReplacementService,
  type Slot,
} from '../services/appointment/model';
import { startOfWeekDayToNumber } from '../helpers/appHelper';
import { useStateDispatch, useStateSelector } from '../store/';
import {
  selectAppOperation,
  selectSelectedOriginalService,
  selectSelectedTimeSlot,
} from '../reducers/selectors';
import analyticsApiClient from '../services/analytics';
import {
  clearSelectedTimeSlot,
  updateSelectedTimeSlot,
} from '../reducers/app/appReducer';
import { clearSlotErrors } from '../reducers/timeslots/timeSlotsReducer';
import {
  getFirstTimeSlotId,
  hasPendingSlot,
  isCompleted,
} from '../helpers/slotHelper';
import { type HandlePendingSlotSelected } from './useGetAvailableTimeslots';
import { differenceInCalendarWeeks } from 'date-fns';
import { sendEventToOptimizely } from '../optimizely';

interface CalendarState {
  calendarDays: CalendarDay[];
  selectedDate: string;
  selectedSlot?: Slot;
  selectedReplacementServiceSlot?: Slot;
  showReplacementServiceSlots: boolean;
  numberOfWeeks: number;
  replacementServiceSlotsEnabled: boolean;
}

interface CalendarActions {
  handleSelectTimeSlot: (
    timeSlotId: string,
    isReplacementService?: boolean,
  ) => void;
  handleSelectSlot: (date: string) => void;
  handleSwitchChange: (showReplacementServiceTimeSlots: boolean) => void;
  handleEnableReplacementServiceSlots: () => void;
}

const getDispatchAnalyticsFunc =
  (
    operation: string,
    originalService?: AvailableService,
    replacementService?: ReplacementService,
  ) =>
  (selectType: 'date' | 'time', isReplacementService?: boolean) => {
    const { sendEventToEpisod } = analyticsApiClient();
    const serviceProductId = isReplacementService
      ? replacementService?.serviceId
      : originalService?.serviceId;
    const serviceOperation = isReplacementService
      ? replacementService?.operation
      : operation;
    const eventName = `switching_${selectType}_${serviceProductId}_${serviceOperation}_appointment`;
    sendEventToEpisod(eventName);
    sendEventToOptimizely(eventName);
  };

const isReplacementServiceSlotsSelectable = (
  replacementServiceSlotsEnabled: boolean,
  showReplacementServiceSlots: boolean,
  slots: Slot[],
): boolean =>
  replacementServiceSlotsEnabled &&
  (showReplacementServiceSlots || slots.length === 0);

const getNumberOfWeeksToShow = (startDate: string, slots: Slot[]) => {
  const minWeeksToShow = 4;
  const maxWeeksToShow = 8;
  if (slots.length === 0) {
    return minWeeksToShow;
  }
  let firstWeekWithAvailableTimeSlots = 1;
  const firstDateWithTimeSlots = slots[0].date;
  firstWeekWithAvailableTimeSlots =
    firstWeekWithAvailableTimeSlots +
    differenceInCalendarWeeks(firstDateWithTimeSlots, startDate, {
      weekStartsOn: startOfWeekDayToNumber(CSS_CONFIG.formats.firstDayOfWeek),
    });
  if (
    firstWeekWithAvailableTimeSlots <= minWeeksToShow ||
    firstWeekWithAvailableTimeSlots > maxWeeksToShow
  ) {
    return minWeeksToShow;
  }
  return firstWeekWithAvailableTimeSlots;
};

const initSelectedDate = (calendarDays: CalendarDay[]): string => {
  const { slot } = getFirstAvailableSlot(calendarDays);
  return slot?.date || '';
};

const initShowReplacementServiceSlots = (
  calendarDays: CalendarDay[],
): boolean => {
  const { isReplacementService } = getFirstAvailableSlot(calendarDays);
  return isReplacementService;
};

const { firstDayOfWeek } = CSS_CONFIG.formats;
const WEEK_STARTS_ON = startOfWeekDayToNumber(firstDayOfWeek);

const useCalendar = (
  startDate: string,
  slots: Slot[],
  replacementServiceSlots: Slot[],
  onPendingSlotSelected: HandlePendingSlotSelected,
): [CalendarState, CalendarActions] => {
  const { sendEventToEpisod } = analyticsApiClient();
  const dispatch = useStateDispatch();
  const isInitCalendar = useRef(true);
  const operation = useStateSelector(selectAppOperation);
  const selectedTimeSlot = useStateSelector(selectSelectedTimeSlot);
  const originalService = useStateSelector(selectSelectedOriginalService);
  const { replacementService } = originalService || {};
  const numberOfWeeks = getNumberOfWeeksToShow(startDate, slots);
  const [replacementServiceSlotsEnabled, setReplacementServiceSlotsEnabled] =
    useState(slots.length > 0 && replacementServiceSlots.length > 0);
  const calendarDays = useMemo(
    () =>
      getCalendarDays(
        startDate,
        WEEK_STARTS_ON,
        numberOfWeeks,
        slots,
        replacementServiceSlots,
        replacementServiceSlotsEnabled,
      ),
    [
      startDate,
      numberOfWeeks,
      slots,
      replacementServiceSlots,
      replacementServiceSlotsEnabled,
    ],
  );
  const [selectedDate, setSelectedDate] = useState<string>(() =>
    initSelectedDate(calendarDays),
  );
  const [showReplacementServiceSlots, setShowReplacementServiceSlots] =
    useState(() => initShowReplacementServiceSlots(calendarDays));

  const {
    slot: selectedSlot,
    replacementServiceSlot: selectedReplacementServiceSlot,
  } = getCalendarDayByDate(calendarDays, selectedDate) || {};

  const dispatchAnalytics = getDispatchAnalyticsFunc(
    operation,
    originalService,
    replacementService,
  );

  const handleSelectTimeSlot = (
    timeSlotId: string,
    isReplacementService?: boolean,
  ) => {
    if (timeSlotId) {
      dispatchAnalytics('time', isReplacementService);
      dispatch(
        updateSelectedTimeSlot({
          slots: isReplacementService ? replacementServiceSlots : slots,
          id: timeSlotId,
          isReplacementTimeSlot: !!isReplacementService,
        }),
      );
    } else {
      dispatch(clearSelectedTimeSlot());
    }
  };

  const handleSelectSlot = (date: string) => {
    if (date === selectedDate) {
      return;
    }

    setSelectedDate(date);
    dispatch(clearSlotErrors());
    dispatch(clearSelectedTimeSlot());

    const {
      slot: nextSelectedSlot,
      replacementServiceSlot: nextSelectedReplacementServiceSlot,
    } = getCalendarDayByDate(calendarDays, date) || {};
    const isReplacementService =
      showReplacementServiceSlots && !nextSelectedSlot;

    dispatchAnalytics('date', isReplacementService);

    if (hasPendingSlot(nextSelectedSlot, nextSelectedReplacementServiceSlot)) {
      onPendingSlotSelected(date);
    } else {
      const timeSlotId = isReplacementService
        ? getFirstTimeSlotId(nextSelectedReplacementServiceSlot)
        : getFirstTimeSlotId(nextSelectedSlot);
      if (timeSlotId) {
        dispatch(
          updateSelectedTimeSlot({
            slots: isReplacementService ? replacementServiceSlots : slots,
            id: timeSlotId,
            isReplacementTimeSlot: !!isReplacementService,
          }),
        );
      }
    }
  };

  const handleSwitchChange = (
    showReplacementServiceTimeSlots: boolean,
    shouldSkipAnalytics?: boolean,
  ) => {
    if (
      !showReplacementServiceTimeSlots &&
      selectedTimeSlot?.isReplacementService
    ) {
      const timeSlotId = getFirstTimeSlotId(selectedSlot);
      handleSelectTimeSlot(timeSlotId, showReplacementServiceTimeSlots);
    } else if (showReplacementServiceTimeSlots && !selectedTimeSlot?.id) {
      const timeSlotId = getFirstTimeSlotId(selectedReplacementServiceSlot);
      handleSelectTimeSlot(timeSlotId, showReplacementServiceTimeSlots);
    }
    setShowReplacementServiceSlots(showReplacementServiceTimeSlots);
    if (showReplacementServiceTimeSlots && !shouldSkipAnalytics) {
      const eventName = `toggle_replacement_${replacementService?.serviceId}_${operation}_appointment`;
      sendEventToEpisod(eventName);
      sendEventToOptimizely(eventName);
    }
  };

  const handleEnableReplacementServiceSlots = () => {
    const eventName = `refresh_replacement_${replacementService?.serviceId}_${operation}_appointment`;
    sendEventToEpisod(eventName);
    sendEventToOptimizely(eventName);
    setReplacementServiceSlotsEnabled(true);
    handleSwitchChange(true, true);
  };

  useEffect(() => {
    if (!selectedTimeSlot) {
      const date = selectedDate || initSelectedDate(calendarDays);
      const { slot, replacementServiceSlot } =
        getCalendarDayByDate(calendarDays, date) || {};
      const isPending = hasPendingSlot(slot, replacementServiceSlot);

      if (isPending) {
        if (isInitCalendar.current) {
          isInitCalendar.current = false;
          onPendingSlotSelected(selectedDate);
        }
      } else {
        if (isCompleted(slot)) {
          const id = getFirstTimeSlotId(slot);
          dispatch(
            updateSelectedTimeSlot({
              slots,
              id,
              isReplacementTimeSlot: false,
            }),
          );
        } else if (
          isReplacementServiceSlotsSelectable(
            replacementServiceSlotsEnabled,
            showReplacementServiceSlots,
            slots,
          ) &&
          isCompleted(replacementServiceSlot)
        ) {
          const id = getFirstTimeSlotId(replacementServiceSlot);
          dispatch(
            updateSelectedTimeSlot({
              slots: replacementServiceSlots,
              id,
              isReplacementTimeSlot: true,
            }),
          );
          // Syncing local toggle state to match the auto-selected replacement slot
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setShowReplacementServiceSlots(true);
        }

        if (!selectedDate) {
          setSelectedDate(date);
        }
      }

      isInitCalendar.current = false;
    }
    // Re-evaluate when calendarDays is updated to auto select a time slot if
    // none is selected
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarDays]);

  return [
    {
      selectedDate,
      selectedSlot,
      selectedReplacementServiceSlot,
      calendarDays,
      showReplacementServiceSlots,
      numberOfWeeks,
      replacementServiceSlotsEnabled,
    },
    {
      handleSelectTimeSlot,
      handleSelectSlot,
      handleSwitchChange,
      handleEnableReplacementServiceSlots,
    },
  ];
};

export default useCalendar;
